/*
 * stone-filter-ui.js — Custom Stone Filter UI module (Phase 2 unified)
 *
 * Reads config from #stone-filter-root data-* attrs, loads the unified
 * snapshot via StoneFilter.data.loadAll(), pre-applies a baseShape filter
 * when locked_shape is set, and renders the filter sidebar / product grid.
 *
 * Internal field names:
 *   p.baseShape, p.specialty, p.carat, p.caratBucket, p.color, p.clarity,
 *   p.cutGrade, p.available, p.price, p.image, p.handle, p.title
 *
 * URL params (Stylist contract):
 *   ?shape=…&color=…&clarity=…&cut=…&specialty=…&carat=min-max
 *   &price=min-max&availability=…&sort=…
 */
(function () {
  'use strict';

  const SF = window.StoneFilter;
  if (!SF || !SF.data || !SF.api) {
    console.error('[stone-filter] data module not loaded');
    return;
  }

  const root = document.getElementById('stone-filter-root');
  if (!root) return;

  // ─── Config from data-attrs ────────────────────────────────────
  const ctx = {
    collectionHandle: root.dataset.collectionHandle,
    storefrontToken:  root.dataset.storefrontToken,
    shopDomain:       root.dataset.shopDomain,
    cacheVersion:     root.dataset.cacheVersion || 'v3',
    lockedShape:      (root.dataset.lockedShape || '').trim(),
    sourceCollectionHandle: root.dataset.sourceCollectionHandle || '',
    snapshotUrl:      root.dataset.snapshotUrl || '',
  };
  const PAGE_SIZE = parseInt(root.dataset.pageSize, 10) || 60;
  const BACKGROUND_REFRESH = (root.dataset.backgroundRefresh || '').toLowerCase() === 'true';
  const LOCKED_SHAPE = ctx.lockedShape || null;   // string or null

  if (!ctx.storefrontToken) {
    console.warn('[stone-filter] missing storefront token; live fallback will fail if snapshot is unreachable');
  }

  // ─── Cut Grade label map ───────────────────────────────────────
  const CUT_GRADE_LABELS = { 'ID': 'Ideal', 'EX': 'Excellent', 'VG': 'Very Good' };
  const CUT_GRADE_ORDER  = ['ID', 'EX', 'VG'];

  const COLOR_ORDER   = ['D', 'E', 'F', 'G', 'H', 'I', 'J'];
  const CLARITY_ORDER = ['IF', 'VVS1', 'VVS2', 'VS1', 'VS2', 'SI1', 'SI2'];

  // Standard order for Shape filter when not locked
  const SHAPE_ORDER = ['Round', 'Oval', 'Princess', 'Cushion', 'Emerald', 'Radiant', 'Asscher', 'Marquise', 'Pear', 'Heart'];

  // Sort options. Default is price-asc (commerce-friendly: cheapest first).
  // state.sort === null means "use the default".
  const DEFAULT_SORT = 'price-asc';
  const SORT_FNS = {
    'price-asc':  (a, b) => (a.price ?? Infinity)  - (b.price ?? Infinity),
    'price-desc': (a, b) => (b.price ?? -Infinity) - (a.price ?? -Infinity),
    'carat-asc':  (a, b) => (a.carat ?? Infinity)  - (b.carat ?? Infinity),
    'carat-desc': (a, b) => (b.carat ?? -Infinity) - (a.carat ?? -Infinity),
  };

  // ─── DOM ───────────────────────────────────────────────────────
  const $ = (sel) => root.querySelector(sel);
  const elFilterBody = $('[data-filter-body]');
  const elGrid       = $('[data-product-grid]');
  const elChips      = $('[data-active-chips]');
  const elCount      = $('[data-result-count]');
  const elPagination = $('[data-pagination]');
  const elBackdrop   = $('[data-drawer-backdrop]');
  const elToggleCount = $('[data-active-filter-count]');
  const elToggleBtn  = $('[data-drawer-open]');
  const elSort       = $('[data-sort]');
  const tplEmpty     = $('[data-empty-template]');
  const tplError     = $('[data-error-template]');

  // ─── State ─────────────────────────────────────────────────────
  let allProducts = [];          // products from snapshot (already scope-narrowed if locked)
  let state = freshState();
  let bounds = { minPrice: 0, maxPrice: 0, minCarat: 0, maxCarat: 0 };
  let caratPresets = [];
  let specialtyOptions = [];

  function freshState() {
    return {
      baseShape: LOCKED_SHAPE,   // locked or null
      availability: null,
      priceMin: null, priceMax: null,
      caratMin: null, caratMax: null,
      lengthMin: null, lengthMax: null,
      widthMin: null, widthMax: null,
      color: [], clarity: [], cutGrade: [], specialty: [],
      sort: null,
      page: 1,
    };
  }

  // ─── Group expand/collapse state ──────────────────────────────
  // First-visit defaults; persisted user choices override via localStorage.
  const GROUP_DEFAULTS = {
    'Availability':   true,
    'Price':          true,
    'Shape':          true,
    'Specialty Cuts': true,
    'Color':          false,
    'Clarity':        false,
    'Cut Grade':      false,
    'Carat':          false,
    'Size (mm)':      false,
  };
  const GROUP_STORAGE_KEY = 'stone-filter-group-state-v1';

  function loadGroupState() {
    try {
      const stored = JSON.parse(window.localStorage.getItem(GROUP_STORAGE_KEY) || '{}');
      return { ...GROUP_DEFAULTS, ...stored };
    } catch { return { ...GROUP_DEFAULTS }; }
  }
  function saveGroupState() {
    try { window.localStorage.setItem(GROUP_STORAGE_KEY, JSON.stringify(groupExpanded)); }
    catch (e) { /* private mode / quota — ignore */ }
  }
  let groupExpanded = loadGroupState();

  // ─── Init ──────────────────────────────────────────────────────
  init().catch(err => {
    console.error('[stone-filter] init failed', err);
    showError();
  });

  async function init() {
    console.log('[stone-filter] init starting. ctx:', ctx, 'LOCKED_SHAPE:', LOCKED_SHAPE);
    state = mergeURLState(state);
    // Force the locked shape AFTER URL merge — user can't override via URL
    if (LOCKED_SHAPE) state.baseShape = LOCKED_SHAPE;
    showSkeleton(true);

    const result = await SF.data.loadAll(ctx);
    let products = result.products || [];

    // Narrow dataset to the locked shape (so other filters/facets only
    // consider relevant products). When unlocked, keep the full set.
    if (LOCKED_SHAPE) {
      const lc = LOCKED_SHAPE.toLowerCase();
      products = products.filter(p => p.baseShape && String(p.baseShape).toLowerCase() === lc);
      console.log(`[stone-filter] Narrowed to ${products.length} products for locked baseShape=${LOCKED_SHAPE}`);
    }
    allProducts = products;

    computeBoundsAndPresets();
    console.log(`[stone-filter] init complete. allProducts=${allProducts.length}, specialtyOptions=${specialtyOptions.length}`, specialtyOptions);

    showSkeleton(false);
    renderSidebar();
    syncSortDropdown();
    renderAll();
    bindGlobalEvents();

    if (BACKGROUND_REFRESH && !result.fromCache) {
      const schedule = window.requestIdleCallback || ((cb) => setTimeout(cb, 1500));
      schedule(async () => {
        try {
          const diff = await SF.data.backgroundRefresh(ctx, allProducts);
          if (diff.changes > 0) applyBackgroundDiff(diff.updates);
        } catch (e) { console.warn('[stone-filter] background refresh failed', e); }
      });
    }
  }

  function applyBackgroundDiff(updates) {
    if (!updates?.length) return;
    const byHandle = new Map(updates.map(u => [u.handle, u]));
    for (const p of allProducts) {
      const u = byHandle.get(p.handle);
      if (!u) continue;
      if ('available' in u) p.available = u.available;
      if ('price' in u && Number.isFinite(u.price)) p.price = u.price;
    }
    computeBoundsAndPresets();
    renderAll();
  }

  function mergeURLState(base) {
    const parsed = SF.api.parseFilterURL(window.location.href);
    return {
      ...base,
      baseShape: parsed.baseShape || base.baseShape,
      availability: parsed.availability,
      priceMin: parsed.priceMin, priceMax: parsed.priceMax,
      caratMin: parsed.caratMin, caratMax: parsed.caratMax,
      lengthMin: parsed.lengthMin, lengthMax: parsed.lengthMax,
      widthMin: parsed.widthMin,   widthMax: parsed.widthMax,
      color: parsed.color.map(c => c.toUpperCase()),
      clarity: parsed.clarity.map(c => c.toUpperCase()),
      cutGrade: parsed.cutGrade.map(c => c.toUpperCase()),
      specialty: parsed.specialty || [],
      sort: parsed.sort,
      page: Number.isFinite(parsed.page) && parsed.page > 0 ? parsed.page : 1,
    };
  }

  function computeBoundsAndPresets() {
    let pMin = Infinity, pMax = -Infinity;
    let cMin = Infinity, cMax = -Infinity;
    let lMin = Infinity, lMax = -Infinity;
    let wMin = Infinity, wMax = -Infinity;
    const bucketSet = new Map();
    const specSet = new Map();
    for (const p of allProducts) {
      if (Number.isFinite(p.price)) {
        if (p.price < pMin) pMin = p.price;
        if (p.price > pMax) pMax = p.price;
      }
      if (Number.isFinite(p.carat)) {
        if (p.carat < cMin) cMin = p.carat;
        if (p.carat > cMax) cMax = p.carat;
      }
      if (Number.isFinite(p.lengthMm) && p.lengthMm > 0) {
        if (p.lengthMm < lMin) lMin = p.lengthMm;
        if (p.lengthMm > lMax) lMax = p.lengthMm;
      }
      if (Number.isFinite(p.widthMm) && p.widthMm > 0) {
        if (p.widthMm < wMin) wMin = p.widthMm;
        if (p.widthMm > wMax) wMax = p.widthMm;
      }
      if (p.caratBucket) bucketSet.set(p.caratBucket, (bucketSet.get(p.caratBucket) || 0) + 1);
      if (p.specialty) specSet.set(p.specialty, (specSet.get(p.specialty) || 0) + 1);
    }
    bounds = {
      minPrice: Number.isFinite(pMin) ? Math.floor(pMin) : 0,
      maxPrice: Number.isFinite(pMax) ? Math.ceil(pMax) : 0,
      minCarat: Number.isFinite(cMin) ? Number(cMin.toFixed(2)) : 0,
      maxCarat: Number.isFinite(cMax) ? Number(cMax.toFixed(2)) : 0,
      minLength: Number.isFinite(lMin) ? Number(lMin.toFixed(1)) : 0,
      maxLength: Number.isFinite(lMax) ? Number(lMax.toFixed(1)) : 0,
      minWidth:  Number.isFinite(wMin) ? Number(wMin.toFixed(1)) : 0,
      maxWidth:  Number.isFinite(wMax) ? Number(wMax.toFixed(1)) : 0,
    };
    caratPresets = [...bucketSet.entries()].map(([label, count]) => ({ label, count }));
    caratPresets.sort(caratPresetSort);
    // Business rule (soft cleanup, not applied to snapshot): drop specialty
    // names containing a digit — DovEggs-compliant cut names never include
    // numerics. The product itself stays in allProducts; it just doesn't
    // surface as a filterable Specialty option.
    specialtyOptions = [...specSet.entries()]
      .filter(([name]) => !/\d/.test(name))
      .sort((a, b) => b[1] - a[1])
      .map(([name, count]) => ({ name, count }));
  }

  function caratPresetSort(a, b) {
    const num = (s) => {
      const m = String(s.label).match(/(\d+(?:\.\d+)?)/);
      return m ? parseFloat(m[1]) : 0;
    };
    return num(a) - num(b);
  }

  // ─── Sidebar rendering ─────────────────────────────────────────
  function renderSidebar() {
    elFilterBody.innerHTML = '';
    elFilterBody.appendChild(group('Availability', availabilityGroup()));
    elFilterBody.appendChild(group('Price', priceGroup()));
    if (!LOCKED_SHAPE) {
      elFilterBody.appendChild(group('Shape', shapeFilterGroup()));
    }
    elFilterBody.appendChild(group('Carat', caratGroup()));
    elFilterBody.appendChild(group('Size (mm)', sizeMmGroup()));
    elFilterBody.appendChild(group('Color', checkboxGroup('color', COLOR_ORDER)));
    elFilterBody.appendChild(group('Clarity', checkboxGroup('clarity', CLARITY_ORDER)));
    elFilterBody.appendChild(group('Cut Grade', cutGradeGroup()));
    if (specialtyOptions.length) {
      console.log('[stone-filter] Rendering Specialty Cuts group with options:', specialtyOptions);
      elFilterBody.appendChild(group('Specialty Cuts', specialtyGroup(), { specialty: true }));
    } else {
      console.warn('[stone-filter] Specialty Cuts group HIDDEN — no products with specialty in loaded dataset.');
    }
    updateActiveFilterCount();
  }

  function countActiveFilters() {
    let n = 0;
    if (state.availability) n++;
    if (state.priceMin != null || state.priceMax != null) n++;
    if (state.caratMin != null || state.caratMax != null) n++;
    if (state.lengthMin != null || state.lengthMax != null) n++;
    if (state.widthMin != null || state.widthMax != null) n++;
    if (!LOCKED_SHAPE && state.baseShape) n++;
    n += (state.color || []).length;
    n += (state.clarity || []).length;
    n += (state.cutGrade || []).length;
    n += (state.specialty || []).length;
    return n;
  }

  function updateActiveFilterCount() {
    if (!elToggleCount) return;
    const n = countActiveFilters();
    if (n > 0) { elToggleCount.textContent = n; elToggleCount.hidden = false; }
    else       { elToggleCount.hidden = true; }
  }

  // All filter groups are collapsible. Initial state for each group is read
  // from groupExpanded (loaded from localStorage at module load; falls back
  // to GROUP_DEFAULTS). Toggling a group updates groupExpanded and persists.
  function group(title, body, opts = {}) {
    const wrap = document.createElement('div');
    wrap.className = 'stone-filter__group' + (opts.specialty ? ' stone-filter__group--specialty' : '');

    const expanded = (title in groupExpanded) ? !!groupExpanded[title] : true;
    if (!expanded) wrap.dataset.collapsed = '1';

    const h = document.createElement('button');
    h.type = 'button';
    h.className = 'stone-filter__group-title';
    h.setAttribute('aria-expanded', expanded ? 'true' : 'false');

    const label = document.createElement('span');
    label.className = 'stone-filter__group-label';
    label.textContent = title;
    h.appendChild(label);

    const chevron = document.createElement('span');
    chevron.className = 'stone-filter__chevron';
    chevron.setAttribute('aria-hidden', 'true');
    h.appendChild(chevron);

    h.addEventListener('click', () => {
      const isCollapsed = wrap.dataset.collapsed === '1';
      if (isCollapsed) {
        delete wrap.dataset.collapsed;
        h.setAttribute('aria-expanded', 'true');
        groupExpanded[title] = true;
      } else {
        wrap.dataset.collapsed = '1';
        h.setAttribute('aria-expanded', 'false');
        groupExpanded[title] = false;
      }
      saveGroupState();
    });

    wrap.appendChild(h);
    const bodyEl = document.createElement('div');
    bodyEl.className = 'stone-filter__group-body';
    bodyEl.appendChild(body);
    wrap.appendChild(bodyEl);
    return wrap;
  }

  function availabilityGroup() {
    const frag = document.createDocumentFragment();
    const options = [
      { key: 'in-stock',     label: 'In stock' },
      { key: 'out-of-stock', label: 'Out of stock' },
    ];
    for (const o of options) {
      const lbl = document.createElement('label');
      lbl.className = 'stone-filter__option';
      const cb = document.createElement('input');
      cb.type = 'radio';
      cb.name = 'availability';
      cb.value = o.key;
      cb.checked = state.availability === o.key;
      cb.addEventListener('change', () => {
        state.availability = cb.checked ? o.key : null;
        onStateChange();
      });
      lbl.appendChild(cb);
      lbl.append(' ' + o.label);
      frag.appendChild(lbl);
    }
    return frag;
  }

  function priceGroup() {
    const frag = document.createDocumentFragment();
    const wrap = document.createElement('div');
    wrap.className = 'stone-filter__range';
    const min = document.createElement('input');
    min.type = 'number'; min.placeholder = `Min ($${bounds.minPrice})`;
    min.value = state.priceMin ?? '';
    const max = document.createElement('input');
    max.type = 'number'; max.placeholder = `Max ($${bounds.maxPrice})`;
    max.value = state.priceMax ?? '';
    const apply = () => {
      state.priceMin = min.value === '' ? null : parseFloat(min.value);
      state.priceMax = max.value === '' ? null : parseFloat(max.value);
      onStateChange();
    };
    min.addEventListener('change', apply);
    max.addEventListener('change', apply);
    wrap.appendChild(min);
    wrap.appendChild(max);
    frag.appendChild(wrap);
    return frag;
  }

  // Shape filter group (single-select; only rendered when not locked)
  function shapeFilterGroup() {
    const frag = document.createDocumentFragment();
    const counts = SF.data.facetCounts(allProducts, state, 'baseShape');

    // "All shapes" option
    const allLbl = document.createElement('label');
    allLbl.className = 'stone-filter__option';
    const allCb = document.createElement('input');
    allCb.type = 'radio';
    allCb.name = 'baseShape';
    allCb.value = '';
    allCb.checked = !state.baseShape;
    allCb.addEventListener('change', () => {
      if (allCb.checked) state.baseShape = null;
      onStateChange();
    });
    allLbl.appendChild(allCb);
    allLbl.append(' All shapes');
    frag.appendChild(allLbl);

    for (const shape of SHAPE_ORDER) {
      const count = counts.get(shape.toLowerCase()) || 0;
      if (count === 0 && state.baseShape !== shape) continue;
      const lbl = document.createElement('label');
      lbl.className = 'stone-filter__option';
      const cb = document.createElement('input');
      cb.type = 'radio';
      cb.name = 'baseShape';
      cb.value = shape;
      cb.checked = state.baseShape === shape;
      cb.disabled = count === 0;
      cb.addEventListener('change', () => {
        if (cb.checked) state.baseShape = shape;
        onStateChange();
      });
      lbl.appendChild(cb);
      lbl.append(' ' + shape);
      const cnt = document.createElement('span');
      cnt.className = 'stone-filter__option-count';
      cnt.textContent = count;
      lbl.appendChild(cnt);
      frag.appendChild(lbl);
    }
    return frag;
  }

  function sizeMmGroup() {
    const frag = document.createDocumentFragment();
    const hint = document.createElement('p');
    hint.className = 'stone-filter__hint';
    hint.textContent = 'Length is the longer dimension. ±0.3 mm tolerance applied.';
    frag.appendChild(hint);

    const mkRow = (label, minState, maxState, minBound, maxBound, minKey, maxKey) => {
      const row = document.createElement('div');
      row.className = 'stone-filter__mm-row';
      const lbl = document.createElement('span');
      lbl.className = 'stone-filter__mm-label';
      lbl.textContent = label;
      row.appendChild(lbl);
      const rangeWrap = document.createElement('div');
      rangeWrap.className = 'stone-filter__range';
      const min = document.createElement('input');
      min.type = 'number'; min.step = '0.1'; min.placeholder = `Min (${minBound})`;
      min.value = minState ?? '';
      const max = document.createElement('input');
      max.type = 'number'; max.step = '0.1'; max.placeholder = `Max (${maxBound})`;
      max.value = maxState ?? '';
      const apply = () => {
        state[minKey] = min.value === '' ? null : parseFloat(min.value);
        state[maxKey] = max.value === '' ? null : parseFloat(max.value);
        onStateChange();
      };
      min.addEventListener('change', apply);
      max.addEventListener('change', apply);
      rangeWrap.appendChild(min);
      rangeWrap.appendChild(max);
      row.appendChild(rangeWrap);
      return row;
    };

    frag.appendChild(mkRow('Length',
      state.lengthMin, state.lengthMax,
      bounds.minLength, bounds.maxLength,
      'lengthMin', 'lengthMax'));
    frag.appendChild(mkRow('Width',
      state.widthMin, state.widthMax,
      bounds.minWidth, bounds.maxWidth,
      'widthMin', 'widthMax'));
    return frag;
  }

  function caratGroup() {
    const frag = document.createDocumentFragment();
    const range = document.createElement('div');
    range.className = 'stone-filter__range';
    const min = document.createElement('input');
    min.type = 'number'; min.step = '0.01'; min.placeholder = `Min (${bounds.minCarat})`;
    min.value = state.caratMin ?? '';
    const max = document.createElement('input');
    max.type = 'number'; max.step = '0.01'; max.placeholder = `Max (${bounds.maxCarat})`;
    max.value = state.caratMax ?? '';
    const apply = () => {
      state.caratMin = min.value === '' ? null : parseFloat(min.value);
      state.caratMax = max.value === '' ? null : parseFloat(max.value);
      onStateChange();
    };
    min.addEventListener('change', apply);
    max.addEventListener('change', apply);
    range.appendChild(min);
    range.appendChild(max);
    frag.appendChild(range);

    if (caratPresets.length) {
      const presetWrap = document.createElement('div');
      presetWrap.className = 'stone-filter__presets';
      for (const p of caratPresets) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'stone-filter__preset';
        btn.textContent = `${p.label} (${p.count})`;
        btn.addEventListener('click', () => {
          const m = String(p.label).match(/(\d+(?:\.\d+)?)\s*ct[\s-]+(\d+(?:\.\d+)?)/i);
          if (m) {
            state.caratMin = parseFloat(m[1]);
            state.caratMax = parseFloat(m[2]);
            onStateChange();
          }
        });
        presetWrap.appendChild(btn);
      }
      frag.appendChild(presetWrap);
    }
    return frag;
  }

  function checkboxGroup(stateKey, order) {
    const frag = document.createDocumentFragment();
    const counts = SF.data.facetCounts(allProducts, state, stateKey);
    const extras = [...counts.keys()].filter(k => !order.map(s => s.toLowerCase()).includes(k));
    const items = [...order, ...extras.map(e => e.toUpperCase())];
    for (const opt of items) {
      const count = counts.get(opt.toLowerCase()) || 0;
      if (count === 0 && !state[stateKey].includes(opt)) continue;
      const lbl = document.createElement('label');
      lbl.className = 'stone-filter__option';
      const cb = document.createElement('input');
      cb.type = 'checkbox';
      cb.value = opt;
      cb.checked = state[stateKey].includes(opt);
      cb.disabled = count === 0 && !cb.checked;
      cb.addEventListener('change', () => {
        const idx = state[stateKey].indexOf(opt);
        if (cb.checked && idx < 0) state[stateKey].push(opt);
        else if (!cb.checked && idx >= 0) state[stateKey].splice(idx, 1);
        onStateChange();
      });
      lbl.appendChild(cb);
      lbl.append(' ' + opt);
      const cnt = document.createElement('span');
      cnt.className = 'stone-filter__option-count';
      cnt.textContent = count;
      lbl.appendChild(cnt);
      frag.appendChild(lbl);
    }
    return frag;
  }

  function cutGradeGroup() {
    const frag = document.createDocumentFragment();
    const counts = SF.data.facetCounts(allProducts, state, 'cutGrade');
    for (const code of CUT_GRADE_ORDER) {
      const label = CUT_GRADE_LABELS[code];
      const count = counts.get(code.toLowerCase()) || 0;
      if (count === 0 && !state.cutGrade.includes(code)) continue;
      const lbl = document.createElement('label');
      lbl.className = 'stone-filter__option';
      const cb = document.createElement('input');
      cb.type = 'checkbox';
      cb.value = code;
      cb.checked = state.cutGrade.includes(code);
      cb.disabled = count === 0 && !cb.checked;
      cb.addEventListener('change', () => {
        const idx = state.cutGrade.indexOf(code);
        if (cb.checked && idx < 0) state.cutGrade.push(code);
        else if (!cb.checked && idx >= 0) state.cutGrade.splice(idx, 1);
        onStateChange();
      });
      lbl.appendChild(cb);
      lbl.append(' ' + label);
      const cnt = document.createElement('span');
      cnt.className = 'stone-filter__option-count';
      cnt.textContent = count;
      lbl.appendChild(cnt);
      frag.appendChild(lbl);
    }
    return frag;
  }

  function specialtyGroup() {
    const frag = document.createDocumentFragment();
    const counts = SF.data.facetCounts(allProducts, state, 'specialty');
    for (const opt of specialtyOptions) {
      const keyLower = opt.name.toLowerCase();
      const count = counts.get(keyLower) || 0;
      const lbl = document.createElement('label');
      lbl.className = 'stone-filter__option';
      const cb = document.createElement('input');
      cb.type = 'checkbox';
      cb.value = keyLower;
      cb.checked = state.specialty.includes(keyLower);
      cb.disabled = count === 0 && !cb.checked;
      cb.addEventListener('change', () => {
        const idx = state.specialty.indexOf(keyLower);
        if (cb.checked && idx < 0) state.specialty.push(keyLower);
        else if (!cb.checked && idx >= 0) state.specialty.splice(idx, 1);
        onStateChange();
      });
      lbl.appendChild(cb);
      lbl.append(' ' + opt.name);
      const cnt = document.createElement('span');
      cnt.className = 'stone-filter__option-count';
      cnt.textContent = count;
      lbl.appendChild(cnt);
      frag.appendChild(lbl);
    }
    return frag;
  }

  // ─── Active chips ──────────────────────────────────────────────
  function renderChips() {
    elChips.innerHTML = '';
    const chips = collectChips();
    for (const c of chips) {
      const chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'stone-filter__chip';
      chip.innerHTML = `${escapeHtml(c.label)} <span aria-hidden="true">×</span>`;
      chip.addEventListener('click', () => { c.remove(); onStateChange(); });
      elChips.appendChild(chip);
    }
  }

  function collectChips() {
    const out = [];
    if (state.availability) out.push({
      label: state.availability === 'in-stock' ? 'In stock' : 'Out of stock',
      remove: () => state.availability = null,
    });
    if (state.priceMin != null || state.priceMax != null) out.push({
      label: `$${state.priceMin ?? '…'}–$${state.priceMax ?? '…'}`,
      remove: () => { state.priceMin = null; state.priceMax = null; },
    });
    if (state.caratMin != null || state.caratMax != null) out.push({
      label: `${state.caratMin ?? '…'}ct–${state.caratMax ?? '…'}ct`,
      remove: () => { state.caratMin = null; state.caratMax = null; },
    });
    if (state.lengthMin != null || state.lengthMax != null) out.push({
      label: `L ${state.lengthMin ?? '…'}–${state.lengthMax ?? '…'} mm`,
      remove: () => { state.lengthMin = null; state.lengthMax = null; },
    });
    if (state.widthMin != null || state.widthMax != null) out.push({
      label: `W ${state.widthMin ?? '…'}–${state.widthMax ?? '…'} mm`,
      remove: () => { state.widthMin = null; state.widthMax = null; },
    });
    // Don't show a chip for locked shape — it's implicit on the page
    if (!LOCKED_SHAPE && state.baseShape) out.push({
      label: `Shape ${state.baseShape}`,
      remove: () => { state.baseShape = null; },
    });
    for (const c of state.color)    out.push({ label: `Color ${c}`,    remove: () => state.color    = state.color.filter(x => x !== c) });
    for (const c of state.clarity)  out.push({ label: `Clarity ${c}`,  remove: () => state.clarity  = state.clarity.filter(x => x !== c) });
    for (const c of state.cutGrade) out.push({ label: `${CUT_GRADE_LABELS[c] || c}`, remove: () => state.cutGrade = state.cutGrade.filter(x => x !== c) });
    for (const c of state.specialty) {
      const orig = specialtyOptions.find(o => o.name.toLowerCase() === c);
      out.push({ label: orig ? orig.name : c, remove: () => state.specialty = state.specialty.filter(x => x !== c) });
    }
    return out;
  }

  // ─── Product grid ──────────────────────────────────────────────
  function renderGrid() {
    const filtered = SF.data.applyFilters(allProducts, state);
    const sortKey = state.sort && SORT_FNS[state.sort] ? state.sort : DEFAULT_SORT;
    const sorted = filtered.slice().sort(SORT_FNS[sortKey]);
    const total = sorted.length;
    elCount.textContent = total === 1 ? '1 stone' : `${total.toLocaleString()} stones`;

    if (total === 0) {
      elGrid.innerHTML = '';
      elGrid.appendChild(tplEmpty.content.cloneNode(true));
      renderPagination(0);
      return;
    }

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    // Clamp page when filters shrink the result set.
    if (state.page > totalPages) state.page = totalPages;
    if (state.page < 1) state.page = 1;

    const start = (state.page - 1) * PAGE_SIZE;
    const visible = sorted.slice(start, start + PAGE_SIZE);
    elGrid.innerHTML = visible.map(productCardHTML).join('');
    renderPagination(totalPages);
  }

  // Smart page list: always show first/last/current ± 1, with "…" gaps.
  function buildPageList(current, total) {
    if (total <= 1) return [];
    const set = new Set([1, total, current, current - 1, current + 1]);
    const pages = [...set].filter(p => p >= 1 && p <= total).sort((a, b) => a - b);
    const out = [];
    for (let i = 0; i < pages.length; i++) {
      if (i > 0 && pages[i] - pages[i - 1] > 1) out.push('…');
      out.push(pages[i]);
    }
    return out;
  }

  function renderPagination(totalPages) {
    if (!elPagination) return;
    if (totalPages <= 1) {
      elPagination.innerHTML = '';
      elPagination.hidden = true;
      return;
    }
    elPagination.hidden = false;
    const cur = state.page;
    const items = buildPageList(cur, totalPages);
    const parts = [];
    parts.push(
      `<button type="button" class="stone-filter__page stone-filter__page--nav" data-page="prev"`
      + (cur <= 1 ? ' disabled aria-disabled="true"' : '')
      + ' aria-label="Previous page">&lsaquo;</button>'
    );
    for (const p of items) {
      if (p === '…') {
        parts.push('<span class="stone-filter__page-ellipsis" aria-hidden="true">…</span>');
      } else {
        const active = p === cur;
        parts.push(
          `<button type="button" class="stone-filter__page${active ? ' stone-filter__page--active' : ''}" `
          + `data-page="${p}"`
          + (active ? ' aria-current="page"' : '')
          + `>${p}</button>`
        );
      }
    }
    parts.push(
      `<button type="button" class="stone-filter__page stone-filter__page--nav" data-page="next"`
      + (cur >= totalPages ? ' disabled aria-disabled="true"' : '')
      + ' aria-label="Next page">&rsaquo;</button>'
    );
    elPagination.innerHTML = parts.join('');
  }

  function gotoPage(p) {
    const target = Math.max(1, Math.floor(Number(p) || 1));
    if (target === state.page) return;
    state.page = target;
    pushURL();
    renderGrid();
    // Scroll the grid into view at the top of the viewport (with header offset).
    const rect = elGrid.getBoundingClientRect();
    const top = window.pageYOffset + rect.top - 80;
    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  }

  // Keep the <select> in sync with state.sort (null → default).
  function syncSortDropdown() {
    if (!elSort) return;
    elSort.value = state.sort && SORT_FNS[state.sort] ? state.sort : DEFAULT_SORT;
  }

  function productCardHTML(p) {
    const meta = [
      Number.isFinite(p.carat) ? `${p.carat.toFixed(2)}ct` : null,
      p.color ? `Color ${p.color}` : null,
      p.clarity ? p.clarity : null,
    ].filter(Boolean).join(' · ');
    const badge = p.specialty
      ? `<div class="stone-filter__card-badge">${escapeHtml(p.specialty)}</div>`
      : '';
    const price = Number.isFinite(p.price)
      ? `$${p.price.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`
      : '';
    const img = p.image
      ? `<img class="stone-filter__card-image" src="${escapeHtml(p.image)}" alt="${escapeHtml(p.title)}" loading="lazy">`
      : `<div class="stone-filter__card-image" aria-hidden="true"></div>`;
    return `
      <a class="stone-filter__card" href="/products/${escapeHtml(p.handle)}">
        ${img}
        <div class="stone-filter__card-body">
          <h3 class="stone-filter__card-title">${escapeHtml(p.title)}</h3>
          <div class="stone-filter__card-meta">${escapeHtml(meta)}</div>
          ${badge}
          <div class="stone-filter__card-price">${price}</div>
        </div>
      </a>`;
  }

  function onStateChange() {
    // Filter / sort change invalidates current page — reset to 1.
    state.page = 1;
    pushURL();
    renderAll();
  }

  function renderAll() {
    renderSidebar();
    renderChips();
    syncSortDropdown();
    renderGrid();
  }

  function pushURL() {
    const url = SF.api.buildFilterURL(window.location.pathname, {
      baseShape: state.baseShape,
      color: state.color.map(c => c.toLowerCase()),
      clarity: state.clarity.map(c => c.toLowerCase()),
      cutGrade: state.cutGrade.map(c => c.toLowerCase()),
      specialty: state.specialty,
      caratMin: state.caratMin, caratMax: state.caratMax,
      priceMin: state.priceMin, priceMax: state.priceMax,
      lengthMin: state.lengthMin, lengthMax: state.lengthMax,
      widthMin: state.widthMin,   widthMax: state.widthMax,
      availability: state.availability,
      sort: state.sort,
      page: state.page,
    });
    if (url !== window.location.pathname + window.location.search) {
      history.pushState(state, '', url);
    }
  }

  function bindGlobalEvents() {
    root.addEventListener('click', (e) => {
      const t = e.target;
      if (t.closest('[data-clear-all]')) {
        state = freshState();
        onStateChange();
      }
      const pageBtn = t.closest('[data-page]');
      if (pageBtn && !pageBtn.disabled) {
        const v = pageBtn.dataset.page;
        if (v === 'prev')      gotoPage(state.page - 1);
        else if (v === 'next') gotoPage(state.page + 1);
        else                   gotoPage(parseInt(v, 10));
        return;
      }
      if (t.closest('[data-retry]')) {
        init().catch(() => showError());
      }
      if (t.closest('[data-drawer-open]')) openDrawer();
      if (t.closest('[data-drawer-close]') || t.closest('[data-drawer-apply]')) closeDrawer();
    });

    if (elBackdrop) elBackdrop.addEventListener('click', closeDrawer);
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && root.classList.contains('stone-filter--drawer-open')) closeDrawer();
    });
    if (elSort) {
      elSort.addEventListener('change', () => {
        // Default → store null so it's omitted from the URL.
        state.sort = elSort.value === DEFAULT_SORT ? null : elSort.value;
        onStateChange();
      });
    }
    window.addEventListener('popstate', () => {
      state = mergeURLState(state);
      if (LOCKED_SHAPE) state.baseShape = LOCKED_SHAPE;
      syncSortDropdown();
      renderAll();
    });
  }

  function openDrawer() {
    root.classList.add('stone-filter--drawer-open');
    if (elBackdrop) elBackdrop.hidden = false;
    if (elToggleBtn) elToggleBtn.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
  }
  function closeDrawer() {
    root.classList.remove('stone-filter--drawer-open');
    if (elBackdrop) elBackdrop.hidden = true;
    if (elToggleBtn) elToggleBtn.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
  }

  function showSkeleton(show) { root.classList.toggle('stone-filter--loading', show); }
  function showError() {
    showSkeleton(false);
    elGrid.innerHTML = '';
    elGrid.appendChild(tplError.content.cloneNode(true));
  }

  function escapeHtml(s) {
    return String(s ?? '').replace(/[&<>"']/g, (c) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
    }[c]));
  }
})();
